/**
 * ===================================================================================================
 * [파일명] CoordinationTab/index.js - '협업' 탭의 메인 컨테이너 컴포넌트
 * ===================================================================================================
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { flushSync } from 'react-dom';
import { io } from 'socket.io-client';
import { auth } from '../../../config/firebaseConfig';
import { useCoordination } from '../../../hooks/useCoordination';
import { useCoordinationModals } from '../../../hooks/useCoordinationModals';
import { useTravelMode } from '../../../hooks/useTravelMode';
import { coordinationService } from '../../../services/coordinationService';

// Utils
import { translateEnglishDays } from '../../../utils';
import { isRoomOwner, calculateEndTime, days, getHourFromSettings } from '../../../utils/coordinationUtils';
import { getViewMode } from '../../../utils/coordinationModeUtils';
import {
  handleResetCarryOverTimes,
  handleResetCompletedTimes,
  handleRunAutoSchedule,
  handleValidateScheduleWithTransportMode
} from '../../../utils/coordinationHandlers';

// Components
import TimetableGrid from '../../timetable/TimetableGrid';
import CoordinationCalendarView from '../../calendar/CoordinationCalendarView';
import CoordinationDetailGrid from '../../calendar/CoordinationDetailGrid';
import MemberList from '../../coordination/MemberList';
import AutoSchedulerPanel from '../../scheduler/AutoSchedulerPanel';
import AutoConfirmBanner from '../../coordination/AutoConfirmBanner';

// Modals
import RoomCreationModal from '../../modals/RoomCreationModal';
import RoomManagementModal from '../../modals/RoomManagementModal';
import RequestSlotModal from '../../modals/RequestSlotModal';
import ChangeRequestModal from '../../modals/ChangeRequestModal';
import CustomAlertModal from '../../modals/CustomAlertModal';
import NotificationModal from '../../modals/NotificationModal';
import MemberStatsModal from '../../modals/MemberStatsModal';
import MemberScheduleModal from '../../modals/MemberScheduleModal';
import OptimalTimeModal from '../../modals/OptimalTimeModal';

import ChainExchangeRequestModal from '../../coordination/ChainExchangeRequestModal';

// Local modules
import { useAlertState } from './hooks/useAlertState';
import { useRequests, useRoomExchangeCounts } from './hooks/useRequests';
import { useViewState } from './hooks/useViewState';
import { useSchedulerState, useMemberModalState } from './hooks/useSchedulerState';
import {
  createHandleRequestSlot,
  createHandleCancelRequest,
  createHandleRequestWithUpdate,
  createHandleRequestFromModal,
  createHandleChangeRequest
} from './handlers/requestHandlers';
import {
  RoomHeader,
  TimetableControls,
  RequestSection,
  RoomList,
  ScheduleErrorAlert,
  UnassignedMembersAlert,
  ConflictSuggestionsAlert,
  WarningsAlert,
  TravelErrorAlert,
  LoadingSpinner,
  ErrorDisplay
} from './components';
import { Clock, Users, Mail, Settings, MessageSquare, X, Menu as MenuIcon, Info } from 'lucide-react';
import ChatBox from '../../chat/ChatBox';
import { useChatEnhanced } from '../../../hooks/useChat/enhanced';
import ConversationalRoomView from '../../coordination/ConversationalRoomView';
import { API_BASE_URL } from '../../../config/apiConfig';

// markAsRead 헬퍼: auth.currentUser가 null이면 Firebase 복원을 최대 5초 대기
// iOS에서 앱 재개 시 Firebase auth state가 아직 복원 중일 수 있음
const doMarkAsRead = async (roomId) => {
  try {
    let currentUser = auth.currentUser;
    if (!currentUser) {
      // auth state 복원 대기 (최대 5초)
      currentUser = await new Promise((resolve) => {
        const timer = setTimeout(() => { unsub(); resolve(null); }, 5000);
        const unsub = auth.onAuthStateChanged((user) => {
          clearTimeout(timer);
          unsub();
          resolve(user);
        });
      });
    }
    if (!currentUser) { console.warn('[doMarkAsRead] 사용자 없음 → 중단'); return; }
    const token = await currentUser.getIdToken();
    const res = await fetch(`${API_BASE_URL}/api/chat/${roomId}/read`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      keepalive: true,
    });
    if (!res.ok) {
      const body = await res.text().catch(() => '(읽기 실패)');
      console.warn('[doMarkAsRead] 서버 오류 응답 본문:', body);
    }
  } catch (e) {
    console.warn('[doMarkAsRead] 예외:', e.message);
  }
};

/**
 * [CoordinationTab]
 */
const CoordinationTab = ({ user, onExchangeRequestCountChange, hideHeader = false, initialClear = false, isMobile = false, onRoomStatusChange }) => {
  const { customAlert, showAlert, closeAlert } = useAlertState();
  const { sentRequests, receivedRequests, setSentRequests, setReceivedRequests, loadSentRequests, loadReceivedRequests, chainExchangeRequests, setChainExchangeRequests, loadChainExchangeRequests } = useRequests(user);

  const isConfirmingRef = useRef(false);
  const isHeaderBackRef = useRef(false); // 헤더 뒤로가기 버튼 클릭 시 popstate 중복 처리 방지용
  const myRoomsRef = useRef(null); // handleOpenSpecific에서 stale closure 없이 최신 myRooms 접근용
  const justReadRoomsRef = useRef(null); // { [roomId]: timestamp } — roomListUpdated가 낙관적 업데이트를 덮어쓰는 것을 방지
  // null이면 localStorage에서 초기값 로드: 앱 재시작 후에도 justRead 보호 유지
  if (justReadRoomsRef.current === null) {
    try { justReadRoomsRef.current = JSON.parse(localStorage.getItem('justReadRoomsStore') || '{}'); }
    catch { justReadRoomsRef.current = {}; }
  }
  const [renderKey, setRenderKey] = useState(0);
  const [mobileTab, setMobileTab] = useState('timetable');
  const [isChatOpen, setIsChatOpen] = useState(false);
  
  const [showChainExchangeModal, setShowChainExchangeModal] = useState(false);
  const [selectedChainRequest, setSelectedChainRequest] = useState(null);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [showClearHistoryConfirm, setShowClearHistoryConfirm] = useState(false);
  const roomToLeaveRef = useRef(null);
  const [friends, setFriends] = useState([]);

  const {
    myRooms, setMyRooms, currentRoom, isLoading, error,
    setCurrentRoom, fetchMyRooms, fetchRoomDetails,
    createRoom, joinRoom, updateRoom, deleteRoom,
    submitTimeSlots, assignTimeSlot, removeTimeSlot,
    createRequest, cancelRequest, handleRequest,
    setAutoConfirmDuration
  } = useCoordination(user?.id, onExchangeRequestCountChange, loadSentRequests, showAlert, initialClear);

  useEffect(() => {
     if (onRoomStatusChange) {
        onRoomStatusChange(!!currentRoom, currentRoom?.mode || null);
     }
  }, [currentRoom, onRoomStatusChange]);

  const { roomExchangeCounts, setRoomExchangeCounts, loadRoomExchangeCounts, getRoomRequestCount } = useRoomExchangeCounts(user, myRooms, receivedRequests);

  const {
    viewMode, setViewMode, showFullDay, setShowFullDay, showMerged, setShowMerged,
    selectedSlots, setSelectedSlots, selectedTab, setSelectedTab,
    requestViewMode, setRequestViewMode, showAllRequests, setShowAllRequests,
    expandedSections, setExpandedSections, scheduleOptions, setScheduleOptions,
    currentWeekStartDate, handleWeekChange
  } = useViewState();

  const {
    isScheduling, setIsScheduling, scheduleError, setScheduleError,
    unassignedMembersInfo, setUnassignedMembersInfo, conflictSuggestions, setConflictSuggestions,
    warnings, setWarnings,
    showDeleteConfirm, setShowDeleteConfirm
  } = useSchedulerState();

  const {
    memberStatsModal, setMemberStatsModal, showMemberScheduleModal, setShowMemberScheduleModal,
    selectedMemberId, setSelectedMemberId
  } = useMemberModalState();

  const {
    showCreateRoomModal, showManageRoomModal,
    showRequestModal, showChangeRequestModal,
    slotToRequest, slotToChange,
    openCreateRoomModal, closeCreateRoomModal,
    openManageRoomModal, closeManageRoomModal,
    closeRequestModal, openChangeRequestModal, closeChangeRequestModal
  } = useCoordinationModals();

  // 모바일 그룹 뷰의 커스텀 이벤트 리스너 추가
  useEffect(() => {
    const handleOpenCreateRoom = () => {
      openCreateRoomModal();
    };

    window.addEventListener('openCreateRoom', handleOpenCreateRoom);

    return () => {
      window.removeEventListener('openCreateRoom', handleOpenCreateRoom);
    };
  }, [openCreateRoomModal]);

  const isOwner = false; // 대화형 모드에서는 방장 개념 없음

  const {
    travelMode,
    handleModeChange: handleTravelModeChangeInternal,
    confirmTravelMode: confirmTravelModeInternal,
    isCalculating: isTravelCalculating,
    error: travelError,
    enhancedSchedule,
    getCurrentScheduleData,
    myTravelDuration
  } = useTravelMode(currentRoom, isOwner, user);

  const handleTravelModeChange = useCallback(async (newMode) => {
    if (newMode === 'normal') { await handleTravelModeChangeInternal(newMode); return; }
    if (!isOwner) { await handleTravelModeChangeInternal(newMode); return; }
    if (currentRoom) {
      try {
        await handleValidateScheduleWithTransportMode(currentRoom, newMode, showAlert, viewMode, currentWeekStartDate);
        await handleTravelModeChangeInternal(newMode);
      } catch (error) { showAlert('검증 오류', 'error'); }
    }
  }, [handleTravelModeChangeInternal, currentRoom, showAlert, viewMode, currentWeekStartDate, isOwner]);

  const handleConfirmTravelMode = useCallback(async () => {
    if (!isOwner) return;
    try {
      const scheduleData = getCurrentScheduleData();
      await coordinationService.applyTravelMode(currentRoom._id, travelMode, scheduleData);
      if (await confirmTravelModeInternal()) {
        showAlert('모드 적용됨', 'success');
        await fetchRoomDetails(currentRoom._id);
      }
    } catch (error) { showAlert(`실패: ${error.message}`, 'error'); }
  }, [confirmTravelModeInternal, travelMode, currentRoom, showAlert, isOwner, getCurrentScheduleData, fetchRoomDetails]);

  const [ownerScheduleCache, setOwnerScheduleCache] = useState(null);

  useEffect(() => {
    if (!isOwner && currentRoom?.currentTravelMode && travelMode !== currentRoom.currentTravelMode) {
      handleTravelModeChange(currentRoom.currentTravelMode);
    }
  }, [isOwner, currentRoom?.currentTravelMode, travelMode, handleTravelModeChange]);

  const [roomModalDefaultTab, setRoomModalDefaultTab] = useState('info');
  const [selectedDate, setSelectedDate] = useState(null);
  const [showDetailGrid, setShowDetailGrid] = useState(false);
  const [showWalkingErrorModal, setShowWalkingErrorModal] = useState(false);
  const [walkingErrorMessage, setWalkingErrorMessage] = useState('');
  const [showOptimalTimeModal, setShowOptimalTimeModal] = useState(false);

  const scheduleStartHour = getHourFromSettings(currentRoom?.settings?.scheduleStart || currentRoom?.settings?.startHour, '9');
  const scheduleEndHour = getHourFromSettings(currentRoom?.settings?.scheduleEnd || currentRoom?.settings?.endHour, '18');
  const effectiveShowFullDay = showFullDay;

  const onRefreshExchangeCount = useCallback(() => loadRoomExchangeCounts(), [loadRoomExchangeCounts]);

  // 대화형 모드에서는 방장 개념 없음 - syncOwnerPersonalTimes 제거

  useEffect(() => {
    if (!currentRoom?._id) return;
    const socket = io(API_BASE_URL, { transports: ['websocket', 'polling'] });
    socket.emit('join-room', currentRoom._id);
    socket.on('schedule-confirmed', async () => {
      await fetchRoomDetails(currentRoom._id, true); // Bug 4 수정: silent=true → 네트워크 오류 시 방 강제 종료 방지
      window.dispatchEvent(new CustomEvent('refreshUser'));
      showAlert('일정이 확정되었습니다.', 'success');
    });
    return () => { socket.disconnect(); };
  }, [currentRoom?._id, fetchRoomDetails, showAlert]);

  // CustomEvent 리스너: 채팅에서 일정 확정 시 새로고침
  useEffect(() => {
    const handleScheduleConfirmed = async () => {
      if (currentRoom?._id) {
        await fetchRoomDetails(currentRoom._id);
        window.dispatchEvent(new CustomEvent('refreshUser'));
      }
    };

    window.addEventListener('schedule-confirmed', handleScheduleConfirmed);
    return () => window.removeEventListener('schedule-confirmed', handleScheduleConfirmed);
  }, [currentRoom?._id, fetchRoomDetails]);

  useEffect(() => {
    if (currentRoom?.owner?.defaultSchedule) {
      setOwnerScheduleCache({
        defaultSchedule: currentRoom.owner.defaultSchedule,
        scheduleExceptions: currentRoom.owner.scheduleExceptions,
        personalTimes: currentRoom.owner.personalTimes,
        _timestamp: Date.now()
      });
      setRenderKey(p => p + 1);
    }
  }, [currentRoom]);

  // myRoomsRef를 최신 myRooms로 항상 동기화 (handleOpenSpecific 클로저에서 stale 방지)
  useEffect(() => { myRoomsRef.current = myRooms; }, [myRooms]);

  useEffect(() => {
    const handleClear = () => { localStorage.removeItem('currentRoomId'); setCurrentRoom(null); };
    const handleRestore = async (e) => { if (e.detail.roomId) { localStorage.setItem('currentRoomId', e.detail.roomId); await fetchRoomDetails(e.detail.roomId); } };
    const handleOpenSpecific = async (e) => {
      const roomId = e.detail?.roomId;
      if (roomId) {
        // SW의 pendingNavigateRoomId 정리: URL·localStorage 경로로 방이 열릴 때도 항상 클리어
        // (postMessage 경로는 module-level 리스너가 이미 처리, 이중 클리어는 무해)
        navigator.serviceWorker?.controller?.postMessage({ type: 'CLEAR_PENDING_NAVIGATE' });
        localStorage.setItem('currentRoomId', roomId);
        // 이미 방 안에 있으면 replaceState (pushState 시 히스토리 중복 → 스와이프 시 같은 방으로 다시 진입하는 버그)
        if (window.location.hash === '#coordination-room') {
          window.history.replaceState({ tab: 'coordination', roomState: 'inRoom', roomId }, '', '#coordination-room');
        } else {
          window.history.pushState({ tab: 'coordination', roomState: 'inRoom', roomId }, '', '#coordination-room');
        }
        // SW에 현재 방 알림 → 이 방의 push 수신 시 팝업 생략 (방 안에서 읽고 있음)
        navigator.serviceWorker?.controller?.postMessage({ type: 'SET_CURRENT_ROOM', roomId });
        // 방 목록에 이미 캐시된 경우 즉시 렌더 → 목록→방 플래시 제거 (mode 없어도 즉시 렌더)
        const cached = [...(myRoomsRef.current?.owned || []), ...(myRoomsRef.current?.joined || [])].find(r => r._id === roomId);
        if (cached) setCurrentRoom(cached);
        const fetchedRoom = await fetchRoomDetails(roomId);
        // roomId가 inviteCode일 수 있으므로 실제 MongoDB _id 사용
        const actualRoomId = fetchedRoom?._id || roomId;
        if (actualRoomId !== roomId) {
          localStorage.setItem('currentRoomId', actualRoomId);
          navigator.serviceWorker?.controller?.postMessage({ type: 'SET_CURRENT_ROOM', roomId: actualRoomId });
        }
        // 방 진입 시 읽음 처리 → 배지 즉시 갱신
        try {
          const token = await auth.currentUser?.getIdToken();
          if (token) {
            const readRes = await fetch(`${API_BASE_URL}/api/chat/${actualRoomId}/read`, {
              method: 'POST',
              headers: { Authorization: `Bearer ${token}` }
            });
            window.dispatchEvent(new Event('unread-count-changed'));
          } else {
            console.warn('[handleOpenSpecific] token 없음, currentUser:', auth.currentUser?.uid || 'null');
          }
        } catch (e) { console.warn('[handleOpenSpecific] markAsRead 예외:', e.message); }
      }
    };
    // App.js 5초 폴링에서 방 목록 데이터 공유 → 별도 API 호출 없이 방 목록 뱃지 실시간 업데이트
    // justReadRoomsRef에 기록된 방은 10초간 unreadCount 0 유지 (낙관적 업데이트 보호)
    const handleRoomListUpdated = (e) => {
      if (!localStorage.getItem('currentRoomId') && e.detail?.data) {
        const now = Date.now();
        const patchUnread = (arr) => (arr || []).map(r => {
          const readAt = justReadRoomsRef.current[r._id];
          if (readAt && now - readAt < 5000) {
            return { ...r, unreadCount: 0 };
          }
          return r;
        });
        setMyRooms({
          owned: patchUnread(e.detail.data.owned),
          joined: patchUnread(e.detail.data.joined),
        });
      }
    };
    const handleRoomListChanged = () => { fetchMyRooms(); };
    window.addEventListener('clearCurrentRoom', handleClear);
    window.addEventListener('restoreRoom', handleRestore);
    window.addEventListener('openSpecificRoom', handleOpenSpecific);
    window.addEventListener('roomListUpdated', handleRoomListUpdated);
    window.addEventListener('roomListChanged', handleRoomListChanged);

    // 마운트 시 방 열기 처리 (우선순위: URL param > localStorage)
    const urlOpenRoom = new URLSearchParams(window.location.search).get('openRoom');
    const pendingRoomId = urlOpenRoom ? null : localStorage.getItem('pendingOpenRoom');
    if (urlOpenRoom) {
      // URL param을 직접 처리: MobileGroupsView event dispatch 타이밍에 의존하지 않음
      // URL 먼저 클리어 → MobileGroupsView useEffect가 실행돼도 중복 dispatch 방지
      window.history.replaceState({}, '', '/groups');
      setTimeout(() => handleOpenSpecific({ detail: { roomId: urlOpenRoom } }), 0);
    } else if (pendingRoomId) {
      localStorage.removeItem('pendingOpenRoom');
      setTimeout(() => handleOpenSpecific({ detail: { roomId: pendingRoomId } }), 0);
    }

    return () => {
      window.removeEventListener('clearCurrentRoom', handleClear);
      window.removeEventListener('restoreRoom', handleRestore);
      window.removeEventListener('openSpecificRoom', handleOpenSpecific);
      window.removeEventListener('roomListUpdated', handleRoomListUpdated);
      window.removeEventListener('roomListChanged', handleRoomListChanged);
    };
  }, [setCurrentRoom, fetchRoomDetails, setMyRooms, fetchMyRooms]);

  useEffect(() => {
    if (user?.id) {
      fetchMyRooms();
      loadRoomExchangeCounts();
      loadSentRequests();
      loadReceivedRequests();
      loadChainExchangeRequests();
    }
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    const pollInterval = setInterval(() => { loadSentRequests(); loadReceivedRequests(); loadChainExchangeRequests(); }, 5000);
    return () => clearInterval(pollInterval);
  }, [user?.id, loadSentRequests, loadReceivedRequests, loadChainExchangeRequests]);

  useEffect(() => {
    if (chainExchangeRequests.length > 0 && !showChainExchangeModal) {
      setSelectedChainRequest(chainExchangeRequests[0]);
      setShowChainExchangeModal(true);
    }
  }, [chainExchangeRequests, showChainExchangeModal]);

  const handleChainExchangeRequestHandled = async () => {
    await loadChainExchangeRequests();
    if (currentRoom?._id) { await fetchRoomDetails(currentRoom._id); }
    loadRoomExchangeCounts();
  };

  useEffect(() => {
    if (!onExchangeRequestCountChange) return;
    const count = currentRoom
      ? receivedRequests.filter(req => req.status === 'pending' && req.roomId === currentRoom?._id).length
      : receivedRequests.filter(req => req.status === 'pending').length;
    onExchangeRequestCountChange(count);
  }, [currentRoom, receivedRequests, onExchangeRequestCountChange]);

  const [eventAddedKey, setEventAddedKey] = useState(0);
  const [eventActions, setEventActions] = useState({ addEvent: async () => {}, deleteEvent: async () => {}, editEvent: async () => {} });
  const [pendingRequest, setPendingRequest] = useState(null);
  const chatEnhanced = useChatEnhanced(!!user, setEventAddedKey, eventActions);

  const handleChatMessage = async (message, context = {}) => {
     try {
        if (!chatEnhanced?.handleChatMessage) return { success: false, message: '준비 중...' };
        const res = await chatEnhanced.handleChatMessage(message, {
           context: 'coordination', roomId: currentRoom?._id, currentEvents: [], pendingRequest, ...context
        });
        if (res.needsConfirmation && res.pendingRequest) setPendingRequest(res.pendingRequest);
        else if (res.clearPending) setPendingRequest(null);
        if (currentRoom?._id) fetchRoomDetails(currentRoom._id, true);
        return res;
     } catch (e) { return { success: false, message: '오류 발생' }; }
  };

  const handleRequestSlot = createHandleRequestSlot(currentRoom, createRequest, fetchRoomDetails, loadSentRequests, showAlert, closeChangeRequestModal);
  const handleCancelRequestCallback = createHandleCancelRequest(setSentRequests, setReceivedRequests, cancelRequest, loadSentRequests, loadReceivedRequests, onRefreshExchangeCount, showAlert);
  const handleRequestWithUpdateCallback = createHandleRequestWithUpdate(handleRequest, currentRoom, fetchRoomDetails, loadReceivedRequests, loadSentRequests, loadRoomExchangeCounts, onRefreshExchangeCount, showAlert);

  const handleCreateRoom = async (d) => {
    const room = await createRoom(d);
    fetchMyRooms();
    if (room?._id) {
      localStorage.setItem('currentRoomId', room._id);
      window.history.pushState({ tab: 'coordination', roomState: 'inRoom', roomId: room._id }, '', '#coordination-room');
      navigator.serviceWorker?.controller?.postMessage({ type: 'SET_CURRENT_ROOM', roomId: room._id });
    }
    return room;
  };
  const handleRoomClick = async (r) => {
    if (r._id) {
      localStorage.setItem('currentRoomId', r._id);
      // mode가 있을 때만 즉시 렌더 (없으면 flash 방지를 위해 fetchRoomDetails 완료 후 렌더)
      if (r.mode) setCurrentRoom(r);
      // pushState: /groups 위에 방 엔트리 추가 → 스와이프 시 방에서 /groups로 돌아올 수 있음
      window.history.pushState({ tab: 'coordination', roomState: 'inRoom', roomId: r._id }, '', '#coordination-room');
      // SW에 현재 방 알림 → 이 방의 push 수신 시 팝업 생략
      navigator.serviceWorker?.controller?.postMessage({ type: 'SET_CURRENT_ROOM', roomId: r._id });
      fetchRoomDetails(r._id);
      // 방 진입 시 즉시 읽음 처리:
      // - lastReadAt을 현재 시간으로 업데이트 → 이후 새 메시지 push에서 배지가 누적되지 않음
      // - 백그라운드 전환 시에도 이 시점 이후 메시지만 카운트됨
      try {
        const token = await auth.currentUser?.getIdToken();
        if (token) {
          const readRes = await fetch(`${API_BASE_URL}/api/chat/${r._id}/read`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` }
          });
          window.dispatchEvent(new Event('unread-count-changed'));
        } else {
          console.warn('[handleRoomClick] token 없음, currentUser:', auth.currentUser?.uid || 'null');
        }
      } catch (e) { console.warn('[handleRoomClick] markAsRead 예외:', e.message); }
    }
    else { setCurrentRoom(r); }
  };

  const handleBackToRoomList = () => {
    // 방 생성 모달(초대 단계)이 열려있으면 닫기 (방 목록 화면에 겹쳐 보이는 버그 방지)
    closeCreateRoomModal();
    const roomId = currentRoom?._id;
    if (roomId) {
      // 1) justReadRoomsRef: handleRoomListUpdated가 10초간 덮어쓰지 못하도록
      // 2) justReadRoomsStore: App.js updateBadge()도 10초간 setAppBadge 보호 (홈화면 아이콘 배지)
      const readAt = Date.now();
      justReadRoomsRef.current[roomId] = readAt;
      try {
        const store = JSON.parse(localStorage.getItem('justReadRoomsStore') || '{}');
        store[roomId] = readAt;
        localStorage.setItem('justReadRoomsStore', JSON.stringify(store));
      } catch {}
      // 3) 낙관적 업데이트: React state 즉시 0으로
      setMyRooms(prev => {
        if (!prev) return prev;
        const clear = arr => (arr || []).map(r => r._id === roomId ? { ...r, unreadCount: 0 } : r);
        return { owned: clear(prev.owned), joined: clear(prev.joined) };
      });
      // 3) localStorage 캐시도 0으로 → 앱 재시작 시 초기 상태가 0
      try {
        const cached = JSON.parse(localStorage.getItem('myRoomsCache') || '{"owned":[],"joined":[]}');
        const clearCache = arr => (arr || []).map(r => r._id === roomId ? { ...r, unreadCount: 0 } : r);
        localStorage.setItem('myRoomsCache', JSON.stringify({ owned: clearCache(cached.owned), joined: clearCache(cached.joined) }));
      } catch {}
    }
    localStorage.removeItem('currentRoomId');
    setCurrentRoom(null);
    navigator.serviceWorker?.controller?.postMessage({ type: 'CLEAR_CURRENT_ROOM' });
    isHeaderBackRef.current = true;
    window.history.back();
    if (roomId) {
      (async () => {
        await doMarkAsRead(roomId);
        // markAsRead 완료 → justRead 보호 즉시 해제
        delete justReadRoomsRef.current[roomId];
        try {
          const store = JSON.parse(localStorage.getItem('justReadRoomsStore') || '{}');
          delete store[roomId];
          localStorage.setItem('justReadRoomsStore', JSON.stringify(store));
        } catch {}
        window.dispatchEvent(new Event('unread-count-changed'));
        // markAsRead 완료 후 fetchMyRooms → 멤버 수 등 최신 데이터 반영
        // (이 시점엔 서버 lastReadAt이 업데이트됐으므로 unreadCount가 0으로 올바르게 반환됨)
        fetchMyRooms();
      })();
    }
  };
  // 앱이 백그라운드로 전환될 때 (홈버튼 / 다른 앱으로 이동) 현재 방 읽음 처리
  // 카카오톡처럼: 방에서 읽고 나가지 않아도 다음 push 배지가 올바르게 카운트됨
  // localStorage를 직접 읽어서 stale closure 문제 방지
  useEffect(() => {
    // SW의 currentRoomId 해제 헬퍼
    // - postMessage만으로는 iOS에서 SW 킬 타이밍에 따라 Cache가 안 지워질 수 있음
    // - 메인 스레드에서 직접 Cache 삭제 → SW 재시작 후 push가 억제되는 버그 방지
    const clearCurrentRoomFromSW = () => {
      if (navigator.serviceWorker?.controller) {
        navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_CURRENT_ROOM' });
      }
      // iOS: SW가 postMessage 처리 전 킬될 수 있으므로 메인 스레드에서도 직접 Cache 삭제
      if ('caches' in window) {
        caches.open('sw-state-v1').then(c => c.delete('/sw-current-room')).catch(() => {});
      }
    };

    const handleVisibilityChange = () => {
      const roomId = localStorage.getItem('currentRoomId');
      if (document.hidden) {
        // 백그라운드 전환: SW의 currentRoomId 해제 → 다음 push 알림이 팝업으로 표시됨
        clearCurrentRoomFromSW();
        // doMarkAsRead를 여기서 호출하지 않음
        // 이유: 백그라운드 중 도착한 새 메시지까지 읽음 처리되는 버그 (서버 처리 지연으로
        //       markAsRead timestamp이 새 메시지 createdAt보다 늦을 수 있음)
        // 읽음 처리는 GroupChat이 담당:
        //   - 방 입장 시 (mount)
        //   - 소켓 메시지 수신 후 1초 딜레이
        //   - 방 나갈 때 (unmount cleanup)
        void roomId; // 미사용 변수 경고 방지
      } else {
        // 포그라운드 복귀: 실제로 방 화면(#coordination-room)에 있을 때만 SW에 다시 알려줌
        // localStorage.currentRoomId만으로 판단하면 방을 나간 후에도 SET이 전송되어
        // 다음 push가 isInThatRoom=true로 억제되는 버그 발생
        if (roomId && window.location.hash === '#coordination-room' && navigator.serviceWorker?.controller) {
          navigator.serviceWorker.controller.postMessage({ type: 'SET_CURRENT_ROOM', roomId });
        }
      }
    };
    // pagehide: iOS에서 visibilitychange보다 신뢰성 높음 (bfcache 동결 시에도 발화)
    // SW 메시지가 처리되기 전 iOS가 SW를 킬하는 경우에도 Cache가 직접 삭제됨
    const handlePageHide = () => clearCurrentRoomFromSW();

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
      // 다른 탭으로 이동 시 CoordinationTab 언마운트 → SW stale roomId 즉시 제거
      // (visibilitychange/pagehide는 언마운트 시 발화 안 함 → 여기서 명시적 클리어)
      clearCurrentRoomFromSW();
      // 탭 이동으로 방에서 나갈 때 localStorage.currentRoomId 정리 + markAsRead 호출
      // handleBackToRoomList/handlePopState는 뒤로가기 시에만 실행됨 → 탭 이동은 여기서만 처리
      // localStorage.currentRoomId가 남아있으면 updateBadge()가 해당 방을 배지에서 영구 제외 → 배지 0 고정 버그
      const roomId = localStorage.getItem('currentRoomId');
      if (roomId) {
        localStorage.removeItem('currentRoomId');
        // justReadRoomsStore 5초 보호: updateBadge()가 stale 서버 데이터로 배지를 덮어쓰는 것 방지
        // (markAsRead API 완료 전 updateBadge 폴링이 실행될 경우 대비)
        try {
          const store = JSON.parse(localStorage.getItem('justReadRoomsStore') || '{}');
          store[roomId] = Date.now();
          localStorage.setItem('justReadRoomsStore', JSON.stringify(store));
        } catch {}
        // doMarkAsRead: 모듈레벨 함수이므로 stale closure 없음 (auth.currentUser를 직접 읽음)
        // GroupChat unmount fire-and-forget이 iOS 중단으로 실패하는 경우를 커버
        doMarkAsRead(roomId).then(() => {
          // markAsRead 완료 → justRead 보호 즉시 해제
          try {
            const store = JSON.parse(localStorage.getItem('justReadRoomsStore') || '{}');
            delete store[roomId];
            localStorage.setItem('justReadRoomsStore', JSON.stringify(store));
          } catch {}
          window.dispatchEvent(new Event('unread-count-changed'));
        }).catch(() => {});
      }
    };
  }, []); // localStorage에서 직접 읽으므로 deps 불필요

  // iOS 스와이프 뒤로가기 감지: popstate 이벤트는 replaceState로는 발생하지 않고
  // 브라우저 자체 뒤로가기(스와이프 포함)에서만 발생함
  // → currentRoom이 설정된 상태에서 popstate가 오면 방을 나가는 것으로 처리
  useEffect(() => {
    const handlePopState = () => {
      // 헤더 뒤로가기 버튼으로 인한 popstate는 handleBackToRoomList에서 이미 처리됨 → 스킵
      if (isHeaderBackRef.current) {
        isHeaderBackRef.current = false;
        return;
      }
      // 방 생성 모달(초대 단계)이 열려있으면 닫기
      closeCreateRoomModal();
      if (!currentRoom) return;
      const roomId = currentRoom._id;
      const readAt = Date.now();
      justReadRoomsRef.current[roomId] = readAt;
      try {
        const store = JSON.parse(localStorage.getItem('justReadRoomsStore') || '{}');
        store[roomId] = readAt;
        localStorage.setItem('justReadRoomsStore', JSON.stringify(store));
      } catch {}
      setMyRooms(prev => {
        if (!prev) return prev;
        const clear = arr => (arr || []).map(r => r._id === roomId ? { ...r, unreadCount: 0 } : r);
        return { owned: clear(prev.owned), joined: clear(prev.joined) };
      });
      try {
        const cached = JSON.parse(localStorage.getItem('myRoomsCache') || '{"owned":[],"joined":[]}');
        const clearCache = arr => (arr || []).map(r => r._id === roomId ? { ...r, unreadCount: 0 } : r);
        localStorage.setItem('myRoomsCache', JSON.stringify({ owned: clearCache(cached.owned), joined: clearCache(cached.joined) }));
      } catch {}
      localStorage.removeItem('currentRoomId');
      setCurrentRoom(null);
      navigator.serviceWorker?.controller?.postMessage({ type: 'CLEAR_CURRENT_ROOM' });
      // controller가 null일 때도 Cache 직접 삭제 (postMessage 유실 방지)
      if ('caches' in window) {
        caches.open('sw-state-v1').then(c => c.delete('/sw-current-room')).catch(() => {});
      }
      (async () => {
        await doMarkAsRead(roomId);
        // markAsRead 완료 → justRead 보호 즉시 해제
        delete justReadRoomsRef.current[roomId];
        try {
          const store = JSON.parse(localStorage.getItem('justReadRoomsStore') || '{}');
          delete store[roomId];
          localStorage.setItem('justReadRoomsStore', JSON.stringify(store));
        } catch {}
        window.dispatchEvent(new Event('unread-count-changed'));
        fetchMyRooms();
      })();
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [currentRoom, fetchMyRooms]);

  // BottomNavigation 새로고침 버튼 + room-updated 실시간 반영
  // - 방 안에 있을 때: fetchRoomDetails (멤버 목록 실시간 갱신)
  // - 방 목록일 때: fetchMyRooms
  useEffect(() => {
    const onRefreshRoomList = () => {
      const currentRoomId = localStorage.getItem('currentRoomId');
      if (currentRoomId) {
        fetchRoomDetails(currentRoomId);
      } else {
        fetchMyRooms();
      }
    };
    window.addEventListener('refreshRoomList', onRefreshRoomList);
    return () => window.removeEventListener('refreshRoomList', onRefreshRoomList);
  }, [fetchMyRooms, fetchRoomDetails]);

  const fetchFriends = useCallback(async () => {
    if (!user?.id) return;
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/friends`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setFriends(data.friends || []);
      }
    } catch {}
  }, [user?.id]);

  useEffect(() => { if (user?.id) fetchFriends(); }, [user?.id, fetchFriends]);

  const handleAddFriend = useCallback(async (targetUserId) => {
    try {
      const token = await auth.currentUser?.getIdToken();
      await fetch(`${API_BASE_URL}/api/friends/request/${targetUserId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchFriends();
    } catch {}
  }, [fetchFriends]);

  const handleRenameRoom = async (newName) => {
    if (!currentRoom?._id || !newName?.trim()) return;
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/coordination/rooms/${currentRoom._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: newName.trim() }),
      });
      if (res.ok) {
        await fetchRoomDetails(currentRoom._id);
        showAlert('방 이름이 변경되었습니다.', 'success');
      } else {
        showAlert('방 이름 변경에 실패했습니다.', 'error');
      }
    } catch {
      showAlert('방 이름 변경 중 오류가 발생했습니다.', 'error');
    }
  };

  const handleLeaveRoom = () => { roomToLeaveRef.current = null; setShowLeaveConfirm(true); };
  const handleLeaveRoomFromList = (room) => { roomToLeaveRef.current = room; setShowLeaveConfirm(true); };
  const executeLeaveRoom = async () => {
    const targetRoom = roomToLeaveRef.current || currentRoom;
    if (!targetRoom?._id) return;
    try {
      const token = await auth.currentUser?.getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/coordination/rooms/${targetRoom._id}/leave`, {
        method: 'DELETE', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
      });
      setShowLeaveConfirm(false);
      roomToLeaveRef.current = null;
      if (response.ok) {
        localStorage.removeItem('currentRoomId');
        if (currentRoom?._id === targetRoom._id) setCurrentRoom(null);
        fetchMyRooms();
      } else {
        const errorData = await response.json();
        showAlert(errorData.msg || '방 나가기 실패', 'error');
      }
    } catch (error) {
      setShowLeaveConfirm(false);
      roomToLeaveRef.current = null;
      showAlert('방 나가기 중 오류가 발생했습니다.', 'error');
    }
  };

  const handleResetCarryOverTimesCallback = useCallback(async () => { await handleResetCarryOverTimes(currentRoom, fetchRoomDetails, setCurrentRoom, showAlert); }, [currentRoom, fetchRoomDetails, setCurrentRoom, showAlert]);
  const handleResetCompletedTimesCallback = useCallback(async () => { await handleResetCompletedTimes(currentRoom, fetchRoomDetails, setCurrentRoom, showAlert); }, [currentRoom, fetchRoomDetails, setCurrentRoom, showAlert]);
  const handleClearAllCarryOverHistoriesCallback = useCallback(() => setShowClearHistoryConfirm(true), []);
  const executeClearAllCarryOverHistories = useCallback(async () => {
    try {
      const res = await coordinationService.clearAllCarryOverHistories(currentRoom._id);
      showAlert(res.msg, 'success'); setCurrentRoom(res.room);
    } catch (error) {
      showAlert('초기화 실패', 'error');
    }
    setShowClearHistoryConfirm(false);
  }, [currentRoom, setCurrentRoom, showAlert]);

  const handleRunAutoScheduleCallback = async () => { await handleRunAutoSchedule(currentRoom, currentWeekStartDate, user, scheduleOptions, setIsScheduling, setScheduleError, setUnassignedMembersInfo, setConflictSuggestions, setWarnings, setCurrentRoom, showAlert, viewMode, travelMode); };
  const handleConfirmSchedule = async () => {
    if (isConfirmingRef.current) return;
    isConfirmingRef.current = true;
    try {
      await coordinationService.confirmSchedule(currentRoom._id, travelMode);
      showAlert('확정되었습니다.', 'success'); await fetchRoomDetails(currentRoom._id);
    } finally { isConfirmingRef.current = false; }
  };

  const executeDeleteAllSlots = async () => {
    if (!currentRoom?._id) return;
    try {
      await coordinationService.deleteAllTimeSlots(currentRoom._id);
      await handleTravelModeChangeInternal('normal');
      await fetchRoomDetails(currentRoom._id);
      showAlert('삭제되었습니다.');
    } catch (error) {
      showAlert(`실패: ${error.message}`, 'error');
    }
    setShowDeleteConfirm(false);
  };

  const openLogsModal = () => { setRoomModalDefaultTab('logs'); openManageRoomModal(); };
  const handleCloseManageRoomModal = () => { closeManageRoomModal(); setRoomModalDefaultTab('info'); };
  const handleMemberClick = (memberId) => { const member = currentRoom?.members?.find(m => (m.user._id || m.user.id) === memberId); if (member) setMemberStatsModal({ isOpen: true, member }); };
  const handleMemberScheduleClick = (memberId) => { setSelectedMemberId(memberId); setShowMemberScheduleModal(true); };
  const handleDateClick = (date) => { setSelectedDate(date); setShowDetailGrid(true); };
  const handleCloseDetailGrid = () => { setShowDetailGrid(false); setSelectedDate(null); };
  const handleCloseWalkingErrorModal = () => { setShowWalkingErrorModal(false); setWalkingErrorMessage(''); };

  const renderCommonModals = () => (
    <>
      {showManageRoomModal && currentRoom && (
        <RoomManagementModal room={currentRoom} onClose={handleCloseManageRoomModal} updateRoom={updateRoom} deleteRoom={deleteRoom} defaultTab={roomModalDefaultTab} onRoomUpdated={(updatedRoom) => { setCurrentRoom(updatedRoom); fetchMyRooms(); }} />
      )}
      {showRequestModal && slotToRequest && (
        <RequestSlotModal onClose={closeRequestModal} onRequest={createHandleRequestFromModal(currentRoom, slotToRequest, handleRequestSlot, closeRequestModal)} slotInfo={slotToRequest} />
      )}
      {showChangeRequestModal && slotToChange && (
        <ChangeRequestModal onClose={closeChangeRequestModal} onRequestChange={createHandleChangeRequest(currentRoom, slotToChange, handleRequestSlot)} slotToChange={slotToChange} />
      )}
      {showCreateRoomModal && (
        <RoomCreationModal onClose={closeCreateRoomModal} onCreateRoom={handleCreateRoom} ownerProfileSchedule={user ? { defaultSchedule: user.defaultSchedule, scheduleExceptions: user.scheduleExceptions, personalTimes: user.personalTimes } : null} user={user} />
      )}
      <CustomAlertModal isOpen={customAlert.show} onClose={closeAlert} title="알림" message={customAlert.message} type={customAlert.type || "warning"} showCancel={false} />
      <MemberStatsModal isOpen={memberStatsModal.isOpen} onClose={() => setMemberStatsModal({ isOpen: false, member: null })} member={memberStatsModal.member} isOwner={isOwner} currentRoom={currentRoom} />
      <CustomAlertModal isOpen={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)} onConfirm={executeDeleteAllSlots} title="삭제" message="전체 삭제하시겠습니까?" type="danger" showCancel={true} />
      {showDetailGrid && selectedDate && (
        <CoordinationDetailGrid selectedDate={selectedDate} timeSlots={getCurrentScheduleData().timeSlots} travelSlots={getCurrentScheduleData().travelSlots || []} travelMode={getCurrentScheduleData().travelMode} members={currentRoom.members || []} currentUser={user} isRoomOwner={isOwner} roomData={currentRoom} showMerged={showMerged} onClose={handleCloseDetailGrid} selectedSlots={[]} onRequestSlot={handleRequestSlot} onRemoveSlot={async (s) => { await removeTimeSlot(currentRoom._id, s.day, s.startTime, s.endTime); await fetchRoomDetails(currentRoom._id); }} ownerOriginalSchedule={ownerScheduleCache} />
      )}
      {showMemberScheduleModal && selectedMemberId && ( <MemberScheduleModal memberId={selectedMemberId} onClose={() => setShowMemberScheduleModal(false)} /> )}
      <ChainExchangeRequestModal isOpen={showChainExchangeModal} onClose={() => setShowChainExchangeModal(false)} request={selectedChainRequest} roomId={selectedChainRequest?.roomId} onRequestHandled={handleChainExchangeRequestHandled} />
      <CustomAlertModal isOpen={showWalkingErrorModal} onClose={handleCloseWalkingErrorModal} title="도보 모드 사용 불가" message={walkingErrorMessage} type="warning" showCancel={false} />
      <OptimalTimeModal isOpen={showOptimalTimeModal} onClose={() => setShowOptimalTimeModal(false)} roomId={currentRoom?._id} />
      <CustomAlertModal isOpen={showLeaveConfirm} onClose={() => { setShowLeaveConfirm(false); roomToLeaveRef.current = null; }} onConfirm={executeLeaveRoom} title="방 나가기" message="정말로 이 방을 나가시겠습니까? 배정된 모든 시간이 삭제됩니다." type="warning" showCancel={true} confirmText="나가기" />
      <CustomAlertModal isOpen={showClearHistoryConfirm} onClose={() => setShowClearHistoryConfirm(false)} onConfirm={executeClearAllCarryOverHistories} title="초기화" message="초기화하시겠습니까?" type="danger" showCancel={true} />
    </>
  );

  if (currentRoom) {
    // mode가 아직 로드되지 않은 경우 (즉시 setCurrentRoom 후 fetchRoomDetails 대기 중)
    // 표준 시간표가 flash되지 않도록 로딩 스피너 표시
    if (!currentRoom.mode) {
      return (
        <div className="flex items-center justify-center h-full" style={{ minHeight: '60vh' }}>
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-500 border-t-transparent" />
        </div>
      );
    }

    const scheduleData = getCurrentScheduleData();

    // 🚀 대화형 모드 분기 (Conversational Mode)
    if (currentRoom.mode === 'conversational') {
      return (
        <>
          <ConversationalRoomView
            currentRoom={currentRoom}
            user={user}
            isOwner={isOwner}
            isMobile={isMobile}
            onManageRoom={openManageRoomModal}
            onBackToRoomList={handleBackToRoomList}
            onLeaveRoom={handleLeaveRoom}
            onMemberClick={handleMemberClick}
            onMemberScheduleClick={handleMemberScheduleClick}
            onFindOptimalTime={() => setShowOptimalTimeModal(true)}
            onRenameRoom={handleRenameRoom}
          />
          {renderCommonModals()}
        </>
      );
    }

    if (isMobile) {
      return (
        <div className="flex flex-col h-[calc(100vh-60px)] bg-gray-50 relative overflow-hidden">
          <RoomHeader currentRoom={currentRoom} user={user} isOwner={isOwner} onManageRoom={openManageRoomModal} onOpenLogs={openLogsModal} onBackToRoomList={handleBackToRoomList} onLeaveRoom={handleLeaveRoom} onRenameRoom={handleRenameRoom} isMobile={true} />
          <div className="flex-1 overflow-y-auto pb-24 relative">
            {mobileTab === 'info' && (
               <div className="p-2 space-y-4">
                  {isOwner && (
                     <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="bg-blue-50 px-4 py-3 border-b border-blue-100 flex items-center">
                           <Settings size={18} className="text-blue-600 mr-2" />
                           <h3 className="font-bold text-blue-800">방 관리</h3>
                        </div>
                        <div className="p-3">
                           <AutoSchedulerPanel options={scheduleOptions} setOptions={setScheduleOptions} onRun={handleRunAutoScheduleCallback} isLoading={isScheduling} currentRoom={currentRoom} onResetCarryOverTimes={handleResetCarryOverTimesCallback} onResetCompletedTimes={handleResetCompletedTimesCallback} onClearAllCarryOverHistories={handleClearAllCarryOverHistoriesCallback} onDeleteAllSlots={() => setShowDeleteConfirm(true)} onConfirmSchedule={handleConfirmSchedule} currentWeekStartDate={currentWeekStartDate} setAutoConfirmDuration={setAutoConfirmDuration} isMobile={true} />
                        </div>
                     </div>
                  )}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                     <div className="bg-orange-50 px-4 py-3 border-b border-orange-100 flex items-center justify-between">
                        <div className="flex items-center">
                           <Mail size={18} className="text-orange-600 mr-2" />
                           <h3 className="font-bold text-orange-800">요청함</h3>
                        </div>
                        {receivedRequests.filter(r => r.status === 'pending').length > 0 && (
                           <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">{receivedRequests.filter(r => r.status === 'pending').length}</span>
                        )}
                     </div>
                     <div className="p-3">
                        <RequestSection currentRoom={currentRoom} currentUser={user} requestViewMode={requestViewMode} setRequestViewMode={setRequestViewMode} receivedRequests={receivedRequests} sentRequests={sentRequests} showAllRequests={showAllRequests} setShowAllRequests={setShowAllRequests} expandedSections={expandedSections} setExpandedSections={setExpandedSections} handleRequestWithUpdate={handleRequestWithUpdateCallback} handleCancelRequest={handleCancelRequestCallback} isMobile={true} />
                     </div>
                  </div>
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                     <div className="bg-green-50 px-4 py-3 border-b border-green-100 flex items-center">
                        <Users size={18} className="text-green-600 mr-2" />
                        <h3 className="font-bold text-green-800">멤버 목록</h3>
                     </div>
                     <div className="p-3">
                        <MemberList currentRoom={currentRoom} user={user} isOwner={isOwner} onMemberClick={handleMemberClick} onMemberScheduleClick={handleMemberScheduleClick} showAlert={showAlert} isMobile={true} />
                     </div>
                  </div>
               </div>
            )}
            {mobileTab === 'timetable' && (
              <div className="p-2">
                <ScheduleErrorAlert scheduleError={scheduleError} />
                <UnassignedMembersAlert unassignedMembersInfo={unassignedMembersInfo} />
                <ConflictSuggestionsAlert conflictSuggestions={conflictSuggestions} />
                {currentRoom?.autoConfirmAt && ( <AutoConfirmBanner key={new Date(currentRoom.autoConfirmAt).getTime()} autoConfirmAt={currentRoom.autoConfirmAt} isOwner={isOwner} /> )}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-2 overflow-hidden">
                  <TimetableControls viewMode={viewMode} setViewMode={setViewMode} showFullDay={showFullDay} setShowFullDay={setShowFullDay} showMerged={showMerged} setShowMerged={setShowMerged} travelMode={travelMode} onTravelModeChange={handleTravelModeChange} onConfirmTravelMode={handleConfirmTravelMode} isTravelCalculating={isTravelCalculating} currentRoom={currentRoom} isOwner={isOwner} scheduleStartHour={scheduleStartHour} scheduleEndHour={scheduleEndHour} isMobile={true} />
                  <TravelErrorAlert travelError={travelError} />
                  {viewMode === 'week' ? (
                    <TimetableGrid key={`week-${effectiveShowFullDay ? 'full' : 'basic'}-${showMerged ? 'merged' : 'split'}-${travelMode}-${renderKey}`} roomId={currentRoom._id} roomSettings={{ ...currentRoom.settings, startHour: effectiveShowFullDay ? 0 : scheduleStartHour, endHour: effectiveShowFullDay ? 24 : scheduleEndHour }} timeSlots={scheduleData.timeSlots} travelSlots={scheduleData.travelSlots || []} travelMode={scheduleData.travelMode} myTravelDuration={scheduleData.myTravelDuration} members={currentRoom.members || []} roomData={currentRoom} currentUser={user} isRoomOwner={isOwner} selectedSlots={[]} onSlotSelect={null} onWeekChange={handleWeekChange} ownerOriginalSchedule={ownerScheduleCache} initialStartDate={currentWeekStartDate} calculateEndTime={calculateEndTime} readOnly={isOwner} showMerged={showMerged} onOpenChangeRequestModal={openChangeRequestModal} isMobile={true} />
                  ) : (
                    <CoordinationCalendarView key={`calendar-${viewMode}-${renderKey}`} roomData={currentRoom} timeSlots={scheduleData.timeSlots} travelSlots={scheduleData.travelSlots || []} travelMode={scheduleData.travelMode} myTravelDuration={scheduleData.myTravelDuration} members={currentRoom.members || []} currentUser={user} isRoomOwner={isOwner} onDateClick={handleDateClick} selectedDate={selectedDate} viewMode={viewMode} currentWeekStartDate={currentWeekStartDate} onWeekChange={handleWeekChange} showFullDay={effectiveShowFullDay} showMerged={showMerged} ownerOriginalSchedule={ownerScheduleCache} isMobile={true} />
                  )}
                </div>
              </div>
            )}
            <button onClick={() => setIsChatOpen(!isChatOpen)} className="fixed bottom-20 right-4 w-14 h-14 bg-blue-600 text-white rounded-full shadow-lg flex items-center justify-center z-50 active:bg-blue-700 transition-all border-4 border-white">
               {isChatOpen ? <X size={28} /> : <MessageSquare size={28} />}
            </button>
            {isChatOpen && (
               <div className="fixed bottom-24 right-4 z-40">
                  <ChatBox 
                     onSendMessage={handleChatMessage} 
                     currentTab="coordination" 
                     onEventUpdate={() => fetchRoomDetails(currentRoom._id, true)} 
                     forceOpen={true} 
                     isMobile={true} 
                  />
               </div>
            )}
          </div>
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 flex justify-around items-center h-16 z-50 px-2 pb-safe shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
            <button onClick={() => setMobileTab('info')} className={`flex flex-col items-center justify-center w-1/2 h-full transition-colors ${mobileTab === 'info' ? 'text-blue-600' : 'text-gray-400'}`} >
              <Info size={24} /> <span className="text-xs mt-1 font-bold">정보</span>
            </button>
            <button onClick={() => setMobileTab('timetable')} className={`flex flex-col items-center justify-center w-1/2 h-full transition-colors ${mobileTab === 'timetable' ? 'text-blue-600' : 'text-gray-400'}`} >
              <Clock size={24} /> <span className="text-xs mt-1 font-bold">시간표</span>
            </button>
          </div>
          {renderCommonModals()}
        </div>
      );
    }

    return (
      <div className="p-1">
        <RoomHeader currentRoom={currentRoom} user={user} isOwner={isOwner} onManageRoom={openManageRoomModal} onOpenLogs={openLogsModal} onBackToRoomList={handleBackToRoomList} onLeaveRoom={handleLeaveRoom} onRenameRoom={handleRenameRoom} />
        <div className="flex gap-2 items-start">
          <div className="flex-shrink-0 flex flex-col" style={{height: 'calc(100vh - 200px)'}}>
              <div className="invisible h-0 overflow-hidden whitespace-nowrap">00000000000000000000000000000000000000000000000000000000</div>
              {isOwner && (
                <AutoSchedulerPanel options={scheduleOptions} setOptions={setScheduleOptions} onRun={handleRunAutoScheduleCallback} isLoading={isScheduling} currentRoom={currentRoom} onResetCarryOverTimes={handleResetCarryOverTimesCallback} onResetCompletedTimes={handleResetCompletedTimesCallback} onClearAllCarryOverHistories={handleClearAllCarryOverHistoriesCallback} onDeleteAllSlots={() => setShowDeleteConfirm(true)} onConfirmSchedule={handleConfirmSchedule} currentWeekStartDate={currentWeekStartDate} setAutoConfirmDuration={setAutoConfirmDuration} />
              )}
              <MemberList currentRoom={currentRoom} user={user} isOwner={isOwner} onMemberClick={handleMemberClick} onMemberScheduleClick={handleMemberScheduleClick} showAlert={showAlert} />
              {!isOwner && (
                <RequestSection currentRoom={currentRoom} currentUser={user} requestViewMode={requestViewMode} setRequestViewMode={setRequestViewMode} receivedRequests={receivedRequests} sentRequests={sentRequests} showAllRequests={showAllRequests} setShowAllRequests={setShowAllRequests} expandedSections={expandedSections} setExpandedSections={setExpandedSections} handleRequestWithUpdate={handleRequestWithUpdateCallback} handleCancelRequest={handleCancelRequestCallback} />
              )}
            </div>
            <div className="flex-grow">
            <ScheduleErrorAlert scheduleError={scheduleError} />
            <UnassignedMembersAlert unassignedMembersInfo={unassignedMembersInfo} />
            <ConflictSuggestionsAlert conflictSuggestions={conflictSuggestions} />
            {currentRoom?.autoConfirmAt && ( <AutoConfirmBanner key={new Date(currentRoom.autoConfirmAt).getTime()} autoConfirmAt={currentRoom.autoConfirmAt} isOwner={isOwner} /> )}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-3 sm:p-4 w-full" style={{height: 'calc(100vh - 200px)', overflow: 'auto'}}>
              <TimetableControls viewMode={viewMode} setViewMode={setViewMode} showFullDay={showFullDay} setShowFullDay={setShowFullDay} showMerged={showMerged} setShowMerged={setShowMerged} travelMode={travelMode} onTravelModeChange={handleTravelModeChange} onConfirmTravelMode={handleConfirmTravelMode} isTravelCalculating={isTravelCalculating} currentRoom={currentRoom} isOwner={isOwner} scheduleStartHour={scheduleStartHour} scheduleEndHour={scheduleEndHour} />
              <TravelErrorAlert travelError={travelError} />
              {viewMode === 'week' ? (
                <TimetableGrid key={`week-${effectiveShowFullDay ? 'full' : 'basic'}-${showMerged ? 'merged' : 'split'}-${travelMode}-${renderKey}`} roomId={currentRoom._id} roomSettings={{ ...currentRoom.settings, startHour: effectiveShowFullDay ? 0 : scheduleStartHour, endHour: effectiveShowFullDay ? 24 : scheduleEndHour }} timeSlots={scheduleData.timeSlots} travelSlots={scheduleData.travelSlots || []} travelMode={scheduleData.travelMode} myTravelDuration={scheduleData.myTravelDuration} members={currentRoom.members || []} roomData={currentRoom} currentUser={user} isRoomOwner={isOwner} selectedSlots={[]} onSlotSelect={null} onWeekChange={handleWeekChange} ownerOriginalSchedule={ownerScheduleCache} initialStartDate={currentWeekStartDate} calculateEndTime={calculateEndTime} readOnly={isOwner} showMerged={showMerged} onOpenChangeRequestModal={openChangeRequestModal} isMobile={true} />
              ) : (
                <CoordinationCalendarView key={`calendar-${viewMode}-${renderKey}`} roomData={currentRoom} timeSlots={scheduleData.timeSlots} travelSlots={scheduleData.travelSlots || []} travelMode={scheduleData.travelMode} myTravelDuration={scheduleData.myTravelDuration} members={currentRoom.members || []} currentUser={user} isRoomOwner={isOwner} onDateClick={handleDateClick} selectedDate={selectedDate} viewMode={viewMode} currentWeekStartDate={currentWeekStartDate} onWeekChange={handleWeekChange} showFullDay={effectiveShowFullDay} showMerged={showMerged} ownerOriginalSchedule={ownerScheduleCache} isMobile={true} />
              )}
            </div>
          </div>
        </div>
        {renderCommonModals()}
      </div>
    );
  }

  return (
    <>
      <RoomList myRooms={myRooms} selectedTab={selectedTab} setSelectedTab={setSelectedTab} roomExchangeCounts={roomExchangeCounts} onRoomClick={handleRoomClick} onCreateRoom={openCreateRoomModal} onLeaveRoom={handleLeaveRoomFromList} hideHeader={hideHeader} />
      {renderCommonModals()}
    </>
  );
};

export default CoordinationTab;